import { getCloudFunctionUrl } from './cloudFunctionUtils';
import { auth } from '../shared/firebase-config';

async function apiCall<T>(functionName: string, options: RequestInit): Promise<T> {
  const url = await getCloudFunctionUrl(functionName);
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('Not authenticated');
  }

  const token = await user.getIdToken();
  
  const response = await fetch(url, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || error.error || 'Request failed');
  }

  return response.json();
}

export async function listAllUsers(): Promise<{ users: any[] }> {
  return apiCall<{ users: any[] }>('listAllUsers', {
    method: 'GET',
  });
}

export async function getUser(userId: string): Promise<{ user: any }> {
  const url = await getCloudFunctionUrl('getUser');
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  
  const response = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || error.error || 'Request failed');
  }

  return response.json();
}

export async function updateUser(userId: string, updates: any): Promise<{ success: boolean; user: any }> {
  return apiCall<{ success: boolean; user: any }>('updateUser', {
    method: 'POST',
    body: JSON.stringify({ userId, updates }),
  });
}

export async function grantFreePro(
  userId: string,
  expirationDate?: string | null
): Promise<{ success: boolean; user: any }> {
  return apiCall<{ success: boolean; user: any }>('grantFreePro', {
    method: 'POST',
    body: JSON.stringify({
      userId,
      ...(expirationDate !== undefined && { expirationDate: expirationDate || null }),
    }),
  });
}

export async function listUserDatasets(userId: string): Promise<{ datasets: any[] }> {
  const url = await getCloudFunctionUrl('listUserDatasets');
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  
  const response = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || error.error || 'Request failed');
  }

  return response.json();
}

export async function deleteDataset(datasetId: string): Promise<{ success: boolean }> {
  return apiCall<{ success: boolean }>('deleteDataset', {
    method: 'POST',
    body: JSON.stringify({ datasetId }),
  });
}

export interface DatasetPreviewTask {
  name: string;
  startDate: string | null;
  endDate: string | null;
  swimlane: string | null;
  isMilestone: boolean;
  progress: number | null;
}

export interface DatasetPreview {
  name: string;
  ownerId: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  taskCount: number;
  swimlanes: string[];
  startDate: string | null;
  endDate: string | null;
  tasksPreview: DatasetPreviewTask[];
}

export async function getDatasetPreview(datasetId: string): Promise<{ preview: DatasetPreview }> {
  return apiCall<{ preview: DatasetPreview }>('getUserDatasetPreview', {
    method: 'POST',
    body: JSON.stringify({ datasetId }),
  });
}

export async function copyDatasetToMyAccount(datasetId: string): Promise<{ id: string; name: string }> {
  return apiCall<{ id: string; name: string }>('copyDatasetToMyAccount', {
    method: 'POST',
    body: JSON.stringify({ datasetId }),
  });
}

export async function getUserUsageStats(userId: string): Promise<{ userId: string; stats: any }> {
  const url = await getCloudFunctionUrl('getUserUsageStats');
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  
  const response = await fetch(`${url}?userId=${encodeURIComponent(userId)}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(error.message || error.error || 'Request failed');
  }

  return response.json();
}

export async function getAllUsersUsageStats(): Promise<{ users: any[] }> {
  return apiCall<{ users: any[] }>('getAllUsersUsageStats', {
    method: 'GET',
  });
}

export async function estimateCosts(
  userId: string | null,
  costPerInputToken: number,
  costPerOutputToken: number,
  overagePricePerInteraction: number
): Promise<{ estimates: any[] }> {
  return apiCall<{ estimates: any[] }>('estimateCosts', {
    method: 'POST',
    body: JSON.stringify({
      userId: userId || undefined,
      costPerInputToken,
      costPerOutputToken,
      overagePricePerInteraction,
    }),
  });
}
